import './Mortgage2ConsultModal.css';

import React from 'react';
import { Modal, Button, Typography } from '@vtblife/uikit';
import { Size } from '@vtblife/uikit/legacy';

import { useUserDetailsLoader } from '@search/auth/src/userDetails/userDetails';
import classname from '@search/classname/src';
import { useStaticPathConfig } from '@search/common-core/src/StaticPathContext';
import { ApplicationIdEnum } from '@search/graphql-typings/src';

import {
    Mortgage2Field,
    MortgageContactFields,
    Mortgage2FieldControls
} from '../field/Mortgage2Field';
import { Mortgage2IconWrap } from '../icon/Mortgage2IconWrap';
import { useMortgage2UserForm } from '../useMortgage2User';
import { getRealtyObjectTypeGql, getSubjectAppName, Mortgage2ContactsFormSendProps, normalizeCallbackUrl, useMortgage2ContactsFormSend } from '../mortgage2ContactsFormSend';

import { useClassifiedOfferDataLayerPush } from '../../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';

import Mortgage2IconSuccess from '../icon/Mortgage2IconSuccess.url.svg';

const cn = classname.bind(null, 'Mortgage2ConsultModal');

export function Mortgage2ConsultModal({
    isMobile,
    onClose,
    sendGtmId = 'classified_card_sale_ipoteka_call_back',
    regionId,
    offerUrl,
    locationPlacement,
    applicationId,
    realtyType,
    discounts,
    showCallbackHours,
    gtmSendData
}: {
    isMobile?: boolean;
    showCallbackHours?: boolean;
    onClose?(e: React.MouseEvent | React.KeyboardEvent): void;
    sendGtmId?: string;
} & Mortgage2ContactsFormSendProps) {
    const staticPath = useStaticPathConfig();
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const userDetailsLoad = useUserDetailsLoader();
    const userInfo = userDetailsLoad({});

    const domain = useMortgage2UserForm({
        isNameRequired: true,
        userDetails: userInfo.data?.userProfile.person
    });
    const mortgage2ContactsFormSend = useMortgage2ContactsFormSend();
    const sendQuery = React.useCallback((e?: React.MouseEvent<Element> | React.KeyboardEvent<HTMLInputElement>) => {
        e?.stopPropagation();
        e?.preventDefault();

        if (! domain.isPhoneValid || ! domain.isNameValid) {
            domain.setFormAllTouched();
            return;
        }

        domain.setFormIsSending();

        mortgage2ContactsFormSend({
            realtyType: getRealtyObjectTypeGql(realtyType),
            regionId,
            applicationId,
            locationPlacement,
            link: normalizeCallbackUrl(offerUrl),
            name: domain.name,
            phone: domain.phone,
            startCallbackHour: domain.workingHoursRange.start,
            endCallbackHour: domain.workingHoursRange.end,
            subject: `Консультация по ипотеке с “${getSubjectAppName(applicationId)}”`,
            discounts
        })
            .then(() => {
                dataLayerPush({ event: sendGtmId });
                domain.setFormSended();
                gtmSendData && dataLayerPush({
                    ...gtmSendData,
                    place: 'consultation'
                });
                if (applicationId === ApplicationIdEnum.ClassifiedDesktop || applicationId === ApplicationIdEnum.ClassifiedTouch) {
                    dataLayerPush({ event: 'up_ib_send_form' });
                    dataLayerPush({ event: 'classified_card_sale_ipoteka_call_back' });
                }
            }).catch(error => {
                domain.setFormSended(error);
            });
    }, [
        domain,
        mortgage2ContactsFormSend,
        realtyType,
        regionId,
        applicationId,
        locationPlacement,
        offerUrl,
        discounts,
        dataLayerPush,
        sendGtmId
    ]);

    const sendQueryOnEnter = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        sendQuery(e);
    }, [ sendQuery ]);

    let content: React.ReactNode | undefined;

    if (domain.isFormSended) {
        content = (<>
            {isMobile ? (
                <>
                    <Modal.Header>
                        Скоро перезвоним
                    </Modal.Header>
                    <Typography color='secondary' className={cn('subtitle')}>
                        Ожидайте, совсем скоро вам перезвонит консультант.
                    </Typography>
                </>
            ) : null}
            {isMobile ? null : (
                <Mortgage2Field
                    titleSize={Size.Large}
                    space={Size.Medium}
                    descriptionAccent
                    title='Скоро перезвоним'
                    children='Ожидайте, совсем скоро вам перезвонит консультант.'
                />
            )}
            <Mortgage2IconWrap>
                <img
                    crossOrigin='anonymous'
                    alt='успешно оставили заявку, благодарный человек'
                    width={265}
                    height={185}
                    src={`${staticPath}${Mortgage2IconSuccess}`}
                />
            </Mortgage2IconWrap>
        </>);
    } else {
        content = (<>
            {isMobile ? (
                <>
                    <Modal.Header>
                        Консультация по ипотеке
                    </Modal.Header>
                    <Typography color='secondary' className={cn('subtitle')}>
                        Оставьте свой телефон, и консультант вам перезвонит.
                    </Typography>
                </>
            ) : null}
            {isMobile ? null : (
                <Mortgage2Field
                    titleSize={Size.Large}
                    space={Size.Medium}
                    descriptionAccent
                    title='Консультация по ипотеке'
                    children='Оставьте свой телефон, и консультант вам перезвонит.'
                />
            )}
            <MortgageContactFields
                isMobile={isMobile}
                domain={domain}
                showCallbackHours={showCallbackHours}
                sendQueryOnEnter={sendQueryOnEnter}
            />
            <Mortgage2FieldControls>
                <Button
                    fullWidth
                    size={isMobile ? 'm' : 'l'}
                    disabled={domain.isFormSyncing}
                    dataTest='mortgage-send-consult'
                    onClick={sendQuery}
                >
                    Перезвоните мне
                </Button>
            </Mortgage2FieldControls>
        </>);
    }

    return (<Modal
        opened
        onClose={onClose}
        mobile={isMobile}
        swipeToClose={! isMobile}
    >{content}</Modal>);
}
